import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import get from 'lodash/get';
import filter from 'lodash/filter';
import {
  Paper,
  Toolbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Switch,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@material-ui/core';

import { getSelectedCandidatesList } from '../../services/sessionTrackServices';
import {
  setCandidateAttendance,
  setCabinetMeetingAttendance,
  setBudgetSeminarAttendance,
} from '../../services/attendanceServices';
import Auth from '../../components/Auth';
import AppShell from '../../components/AppShell';
import Loader from '../../components/Loader';
import SearchBar from '../../components/SearchBar';
import CitySelector from '../../components/CitySelector';

const sessionOptions = [
  { value: 'MONSOON', label: 'Monsoon Session' },
  { value: 'WINTER', label: 'Winter Session' },
  { value: 'BUDGET', label: 'Budget Session' },
];

const dayOptions = [
  { value: 'DAY_1', label: 'Day 1' },
  { value: 'DAY_2', label: 'Day 2' },
  { value: 'DAY_3', label: 'Day 3' },
  { value: 'DAY_4', label: 'Day 4' },
];

const sittingOptions = [
  { value: 'SITTING_1', label: 'Sitting 1' },
  { value: 'SITTING_2', label: 'Sitting 2' },
  { value: 'SITTING_3', label: 'Sitting 3' },
];

class AttendanceSheet extends React.Component {
  static propTypes = {
    authenticatedUserInfo: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      dataLoaded: false,
      candidates: [],
      filteredCandidates: [],
      page: 0,
      rowsPerPage: 10,
      searchResult: [],
      session: 'MONSOON',
      jypCity:
        this.props.authenticatedUserInfo.type === 'ADMIN'
          ? 'OVERALL'
          : this.props.authenticatedUserInfo.location,
      day: 'DAY_1',
      sitting: 'SITTING_1',
    };
  }

  componentDidMount() {
    this.unsubscribe = getSelectedCandidatesList((candidates) => {
      const filteredCandidates = this.getFilteredCandidates(candidates);

      this.setState({
        dataLoaded: true,
        candidates,
        filteredCandidates,
        searchResult: candidates,
      });
    });
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  getFilteredCandidates = (candidates, jypCity) => {
    let city = this.state.jypCity;
    if (jypCity) {
      city = jypCity;
    }

    const filteredCandidates = filter(candidates, (candidateInfo) => {
      if (city === 'OVERALL') {
        return true;
      }
      return candidateInfo.jypCity === city;
    });

    return filteredCandidates;
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value });
  };

  render() {
    const emptyRows = this.state.rowsPerPage
      - Math.min(
        this.state.rowsPerPage,
        this.state.searchResult.length - this.state.page * this.state.rowsPerPage,
      );

    return (
      <div>
        <AppShell>
          {!this.state.dataLoaded ? (
            <Loader />
          ) : (
            <Container>
              <Paper>
                <StyledToolbar>
                  <div>
                    <Typography variant="title" id="tableTitle">
                      Attendance Sheet
                    </Typography>
                    <Typography variant="subheading">
                      {`Candidates:  ${this.state.searchResult.length}`}
                    </Typography>
                  </div>

                  <Row>
                    {this.props.authenticatedUserInfo.type === 'ADMIN' && (
                      <CitySelector
                        initialValue="OVERALL"
                        onChange={(city) => {
                          const { candidates } = this.state;

                          this.setState({
                            jypCity: city,
                            filteredCandidates: this.getFilteredCandidates(candidates, city),
                          });
                        }}
                        disabled={this.props.authenticatedUserInfo.type !== 'ADMIN'}
                        addOverall
                      />
                    )}

                    <SearchBar
                      data={this.state.filteredCandidates}
                      onChange={(searchResult) => {
                        this.setState({ searchResult });
                      }}
                    />
                  </Row>
                </StyledToolbar>

                <StyledToolbar>
                  <StyledFormControl>
                    <InputLabel htmlFor="session">Session</InputLabel>
                    <Select
                      value={this.state.session}
                      onChange={(event) => {
                        const { value } = event.target;

                        this.setState({
                          session: value,
                        });
                      }}
                      inputProps={{
                        name: 'session',
                        id: 'session',
                        placeholder: 'Session',
                      }}
                      autoWidth
                    >
                      {sessionOptions.map(choice => (
                        <MenuItem key={choice.value} value={choice.value}>
                          {choice.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>

                  <StyledFormControl>
                    <InputLabel htmlFor="day">Day</InputLabel>
                    <Select
                      value={this.state.day}
                      onChange={(event) => {
                        const { value } = event.target;

                        this.setState({
                          day: value,
                        });
                      }}
                      inputProps={{
                        name: 'day',
                        id: 'day',
                        placeholder: 'Day',
                      }}
                      autoWidth
                    >
                      {dayOptions.map(choice => (
                        <MenuItem key={choice.value} value={choice.value}>
                          {choice.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>

                  <StyledFormControl>
                    <InputLabel htmlFor="sitting">Sitting</InputLabel>
                    <Select
                      value={this.state.sitting}
                      onChange={(event) => {
                        const { value } = event.target;

                        this.setState({
                          sitting: value,
                        });
                      }}
                      inputProps={{
                        name: 'sitting',
                        id: 'sitting',
                        placeholder: 'Sitting',
                      }}
                      autoWidth
                    >
                      {sittingOptions.map(choice => (
                        <MenuItem key={choice.value} value={choice.value}>
                          {choice.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </StyledToolbar>

                <TableWrapper>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>JYP Candidate ID</TableCell>
                        <TableCell>JYP City</TableCell>
                        <TableCell>Present/Absent</TableCell>
                        {this.state.session !== 'BUDGET' && (
                          <TableCell>Cabinet Meeting Attendance</TableCell>
                        )}
                        {this.state.session === 'BUDGET' && (
                          <TableCell>Budget Seminar Attendance</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {this.state.searchResult
                        .slice(
                          this.state.page * this.state.rowsPerPage,
                          this.state.page * this.state.rowsPerPage + this.state.rowsPerPage,
                        )
                        .map(rowData => (
                          <TableRow key={rowData.id}>
                            <TableCell component="th" scope="row">
                              {rowData.fullName}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {rowData.candidateID}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {rowData.jypCity}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Switch
                                checked={
                                  !!get(
                                    rowData,
                                    `attendance.${this.state.session}.${this.state.day}-${
                                      this.state.sitting
                                    }`,
                                  )
                                }
                                onChange={(event) => {
                                  setCandidateAttendance({
                                    session: this.state.session,
                                    day: this.state.day,
                                    sitting: this.state.sitting,
                                    id: rowData.id,
                                    present: event.target.checked,
                                  });
                                }}
                                value="present"
                              />
                            </TableCell>
                            {this.state.session !== 'BUDGET' && (
                              <TableCell component="th" scope="row">
                                <Switch
                                  checked={
                                    !!get(
                                      rowData,
                                      `attendance.${this.state.session}.cabinetMeeting`,
                                    )
                                  }
                                  onChange={(event) => {
                                    setCabinetMeetingAttendance({
                                      session: this.state.session,

                                      id: rowData.id,
                                      present: event.target.checked,
                                    });
                                  }}
                                  value="cabinet-meeting"
                                />
                              </TableCell>
                            )}
                            {this.state.session === 'BUDGET' && (
                              <TableCell component="th" scope="row">
                                <Switch
                                  checked={!!get(rowData, 'attendance.BUDGET.seminarAttendance')}
                                  onChange={(event) => {
                                    setBudgetSeminarAttendance({
                                      id: rowData.id,
                                      present: event.target.checked,
                                    });
                                  }}
                                  value="seminar-attendance"
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 49 * emptyRows }}>
                          <TableCell colSpan={4} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableWrapper>

                <TablePagination
                  component="div"
                  count={this.state.searchResult.length}
                  rowsPerPage={this.state.rowsPerPage}
                  page={this.state.page}
                  backIconButtonProps={{
                    'aria-label': 'Previous Page',
                  }}
                  nextIconButtonProps={{
                    'aria-label': 'Next Page',
                  }}
                  onChangePage={this.handleChangePage}
                  onChangeRowsPerPage={this.handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 15]}
                />
              </Paper>
            </Container>
          )}
        </AppShell>
      </div>
    );
  }
}

export default () => <Auth>{({ authState }) => <AttendanceSheet {...authState} />}</Auth>;

const Container = styled.div``;

const StyledToolbar = styled(Toolbar)`
  && {
    justify-content: space-between;

    & > * + * {
      margin-left: 30px;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledFormControl = styled(FormControl)`
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 20px;
  }
`;



// WEBPACK FOOTER //
// ./src/pages/AttendanceSheet/AttendanceSheet.js